import PageAuthenticationGuard from '@components/atoms/authentication/page-authentication-guard/page-authentication-guard';
import Button from '@components/atoms/button/button';
import Icon from '@components/atoms/icon/icon';
import Spinner from '@components/atoms/spinner/spinner';
import Head from '@components/atoms/head/head';
import { BrandsInterface } from '@interfaces/models/deposit';
import { useAnalytics } from '@context/analytics.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import extractFromContext from '@helpers/utils/extract-from-context';
import clsx from 'clsx';
import { defaultMetas } from '@constants/metas/default';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useContextSelector } from 'use-context-selector';
import { ListingContext } from '@context/listing.context';
import { usePreferences } from '@context/preferences.context';
import useWindowSize from '@hooks/use-window-size';
import SellLayout from '@layouts/sell-layout';
import DefaultLayout from '@layouts/default-layout';
import useUser from '@hooks/user/use-user';
import { getRegionIdByRegionName } from '@helpers/localization';
import styles from './index.module.scss';

const BRAND_MAX_LENGTH = 60;
/*  
  brand name with highlighted search term
*/
const BrandNameWithHighlight = ({ brandName, searchTerm }) => {
  if (!brandName) {
    return '';
  }
  const searchTermStartIndex = brandName.toLowerCase().indexOf(searchTerm.toLowerCase());
  if (!searchTerm || searchTermStartIndex <= -1) {
    return brandName;
  }
  return (
    <>
      {brandName.substring(0, searchTermStartIndex)}
      <span className={styles['depositForm__form__optionsList__item--strong']}>
        {brandName.substring(searchTermStartIndex, searchTermStartIndex + searchTerm.length)}
      </span>
      {brandName.substring(searchTermStartIndex + searchTerm.length)}
    </>
  );
};

const SellClothesOnlinePage: NextPageWithLayout = () => {
  const { t } = useTranslation('deposit-form');
  const { updateDataLayer } = useAnalytics();
  const { siteId, language } = usePreferences();
  const { sendAnalyticEvent } = useAnalyticEvents('listingForm');
  const { isWiderThanMd, isWiderThanLg, isWiderThanXl } = useWindowSize();
  const { isAuthenticated } = useUser();

  const {
    universes,
    pendingAction,
    existingDraftCount,
    listingDraftPayload,
    setListingDraftAttribute,
    createListingDraft,
    brandsByFilter,
    brandSearchTerm,
    setBrandSearchTerm,
  } = useContextSelector(ListingContext, (state) => state);

  const isKr = siteId === getRegionIdByRegionName('KR');
  const pageMetas = defaultMetas[language];

  const [optionsListIsOpen, setOptionsListIsOpen] = useState<boolean>(false);

  const initialInputBrandsPos = useRef<number>(0);
  const inputBrands = useRef<HTMLInputElement>(null);
  const redirectionUrl = useRef<string | null>(`/journal/start-selling/`);

  /**
   *  State Selectors
   *  Used for getting state from the store.
   */
  const category = useMemo(() => {
    const data = universes?.filter((i) => i.id === listingDraftPayload.universe)[0]?.categories;
    return data || [];
  }, [listingDraftPayload.universe, universes]);

  const [preductId, setPreductId] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingDraft, setLoadingDraft] = useState<boolean>(false);
  const [isBanned, setIsBanned] = useState<boolean>(false);
  const [isDraftSaved, setIsDraftSaved] = useState<boolean>(null);
  const [keyboardSelectIndex, setKeyBoardSelectIndex] = useState<number>(0);

  const listingFormShouldShow = isAuthenticated;

  // Create an element reference for the brands option dropdown.
  const optionsElement = useRef<HTMLUListElement>(null);

  // Memoized function runs when the error changes to return an element.
  const preductFormError = useMemo(() => {
    if (error) {
      return <span className="error-msg">{error}</span>;
    }
    return null;
  }, [error]);

  const handleDraftLoader = () => setLoadingDraft(true);

  /**
   * This effect fires an analytics event when the user is viewing the listing form.
   */
  useEffect(() => {
    updateDataLayer({
      env_template: 'sell_an_item',
      pageUrl: '/submit-an-item',
      screen_category: 'listing_form',
      screen_subcategory: 'submit_an_item',
      screen_name: '/submit_an_item',
    });
  }, []);

  useEffect(() => {
    const { search, pathname } = window.location;
    const query = new URLSearchParams(search);
    const foundDraft = query.get('draft_saved') === 'true';

    setIsDraftSaved(foundDraft);
    query.delete('draft_saved');
    const updatedUrl = query.toString() ? `${pathname}?${query.toString()}` : pathname;
    window.history.pushState(null, '', updatedUrl);
  }, []);

  // This effect handles the redirect once a preduct id is created and sends the analytic event.
  useEffect(() => {
    if (preductId && typeof window !== 'undefined') {
      window.location.href = `${window.location.origin}${t('DEPOSIT_FORM.CONTINUE.URL')}?id=${preductId}`;
    }
  }, [preductId]);

  /**
   * The following function handles closing the dropdown.
   * It adds a class is_closed which sets the reference element display to None
   * @returns void
   */
  const handleCloseOptionsList = () => {
    const classList: DOMTokenList = optionsElement.current?.classList;
    classList?.add('vc-d-none');
    if (classList?.contains('vc-d-none')) {
      setOptionsListIsOpen(false);
    }
  };

  // Add an event listener for dismissing the options list
  useEffect(() => {
    const handleDismissOptions = (e) => {
      if (
        optionsElement.current &&
        !optionsElement.current.contains(e.target as Node) &&
        e.target !== inputBrands.current
      ) {
        handleCloseOptionsList();
      }
    };
    document.addEventListener('click', handleDismissOptions, true);
    return () => document.removeEventListener('click', handleDismissOptions, true);
  }, []);

  // Check if form is valid
  const disabled =
    !(listingDraftPayload.universe && listingDraftPayload.category && listingDraftPayload.brand) || isBanned;

  /**
   *
   * @param e Change event of type any.
   * sets the universe
   * @returns hook setter function
   */
  const handleChangeUniverse = (e) => {
    setListingDraftAttribute({ universe: e.target.value, category: '' });
    sendAnalyticEvent('submit_universe', {
      eventLabel: e.target.dataset?.inputTextValue,
    });
  };

  /**
   *
   * @param e Change event of type HTMLSelectElement
   * sets the category
   * hook setter function
   */
  const handleChangeCategory = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setListingDraftAttribute({ category: e.target.value });
    sendAnalyticEvent('submit_category', {
      eventLabel: e.target.selectedOptions[0]?.innerText,
    });
  };

  /**
   *
   * @param e Form event of type HTMLFormElement
   * prevents default behavior and dispatches a request to create a preduct.
   */
  const handleSubmitForm = async () => {
    sendAnalyticEvent('submit_brand_next');

    if (isAuthenticated) {
      setLoading(true);
      const { draftId, error } = await createListingDraft('listing_form');

      if (error) {
        setError(error);
        setLoading(false);
      }

      if (draftId) {
        sendAnalyticEvent('submit_draft_creation', {
          eventLabel: 'organic',
          preduct_id: draftId,
        });
        setPreductId(draftId);
      }
    }
  };

  const [brandSelectOrigin, setBrandSelectOrigin] = useState<string>('');

  /**
   *
   * @param e Change event of type HTMLInput Elelment
   * Matches brand names to pull the selected brand id. Sets the brand id.
   */
  const handleChangeBrand = (e: React.ChangeEvent<HTMLInputElement>) => {
    optionsElement.current?.classList.remove('vc-d-none');
    setBrandSearchTerm(e.target.value);
    setBrandSelectOrigin('text');
    setListingDraftAttribute({ brand: '' });
    setOptionsListIsOpen(true);
  };

  /**
   *
   * @param brand of type IBrandInterface
   * The function sets the brand id and the serch term for autocompletion effect
   * finally, it closes the options list
   * @returns void
   */
  const handleSelectBrand = (brand: BrandsInterface) => {
    if (!brand) {
      return;
    }
    setBrandSearchTerm(brand.name);
    setListingDraftAttribute({ brand: brand.id });
    setBrandSelectOrigin('list');
    handleCloseOptionsList();
    setIsBanned(brand.banned);
    inputBrands.current?.blur();
  };

  /**
   * Onclick add new brand will set the brand id to 185. This is hardcoded for the deposit web service.
   * finally, it closes the options list
   * @returns void
   */
  const handleAddNewBrand = () => {
    setListingDraftAttribute({ brand: '185', otherBrand: brandSearchTerm });
    sendAnalyticEvent('submit_brand_list', {
      eventLabel: 'brands_text',
      eventProperty: brandSearchTerm,
    });
    handleCloseOptionsList();
    setIsBanned(false);
  };

  /**
   * OnFocus on the Brands input, only for mobile portrait view,
   * scroll to let the place display the brands' suggestion panel.
   */
  const handleFocusBrand = () => {
    if (brandsByFilter) {
      optionsElement.current?.classList.remove('vc-d-none');
    }
    if (!isWiderThanMd) {
      const brandsPanelHeight = 200 + 4; // 200px height + 4px of margin
      const positionToScroll =
        initialInputBrandsPos.current > 0 ? initialInputBrandsPos.current - brandsPanelHeight : 0;
      if (!(isWiderThanLg && !isWiderThanXl) && positionToScroll > 0) {
        if (typeof window !== 'undefined') {
          window.scroll(0, positionToScroll);
        }
      }
    }
  };

  /* 
    handle keydown event for the brands suggestion list
  */
  const handleKeyboardSelect = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!brandsByFilter) {
      return;
    }
    switch (e.key) {
      case 'Enter':
        handleSelectBrand(brandsByFilter[keyboardSelectIndex]);
        return;
      case 'ArrowDown':
        setKeyBoardSelectIndex((prev) => (prev + 1) % brandsByFilter.length);
        return;
      case 'ArrowUp':
        setKeyBoardSelectIndex((prev) => (prev - 1 + brandsByFilter.length) % brandsByFilter.length);
        return;
    }
  };

  useEffect(() => {
    if (listingDraftPayload.brand && brandSelectOrigin && document.activeElement !== inputBrands.current) {
      sendAnalyticEvent('submit_brand_list', {
        eventLabel: `brands_${brandSelectOrigin}`,
        eventProperty: brandSearchTerm,
      });
    }
  }, [brandSelectOrigin, listingDraftPayload.brand]);

  /**
   * If there is an input in the field, there is no selected brand, the brands suggestion is closed, or the brand is banned.
   * The field is in error.
   */
  const brandLengthError = brandSearchTerm.length > BRAND_MAX_LENGTH;
  const brandHasError =
    (brandSearchTerm && !listingDraftPayload.brand && brandSelectOrigin && !optionsListIsOpen) ||
    isBanned ||
    brandLengthError;

  useEffect(() => {
    if (inputBrands && inputBrands.current?.parentElement) {
      initialInputBrandsPos.current = inputBrands.current?.parentElement?.offsetTop;
    }
  });

  return (
    <>
      <Head metadata={pageMetas} />
      <PageAuthenticationGuard redirectionUrl={redirectionUrl.current}>
        <main className={clsx(styles.depositPage)}>
          <div className={clsx(styles.depositForm, 'innerContainer')}>
            {isDraftSaved && (
              <div className={clsx(styles.depositForm__savedBanner)}>
                <Icon
                  name="success-24"
                  className={clsx(styles.depositForm__savedBanner__icon)}
                />
                <p>{t('DEPOSIT_FORM.DRAFT_CONFIRMATION_BANNER')}</p>
              </div>
            )}
            {pendingAction || loading || !isAuthenticated ? (
              <div className={clsx(styles.depositForm__container, styles.depositForm__loadingFeature_container)}>
                <Spinner />
              </div>
            ) : (
              <>
                {!listingFormShouldShow ? (
                  <div className={clsx(styles.depositForm__container, styles.depositForm__loadingFeature_container)}>
                    <Spinner />
                  </div>
                ) : (
                  <>
                    <section className={clsx(styles.depositForm__header)}>
                      <h1 className={clsx(styles.depositForm__title)}>{t('DEPOSIT_FORM.HEADER.TITLE')}</h1>
                      <p className={clsx(styles.depositForm__intro)}>{t('DEPOSIT_FORM.HEADER.INTRO')}</p>
                    </section>
                    <section className={clsx(styles.depositForm__content)}>
                      {existingDraftCount > 0 && (
                        <div
                          className={styles.depositForm__reminder}
                          data-cy="complete-submission"
                        >
                          <p className={styles.depositForm__reminder__text}>
                            {existingDraftCount > 1
                              ? t('DEPOSIT_FORM.ALREADY_STARTED_DRAFTS_PLURAL', {
                                  draftNb: existingDraftCount,
                                })
                              : t('DEPOSIT_FORM.ALREADY_STARTED_DRAFTS_SINGLE')}
                          </p>
                          <div className={styles.depositForm__reminder__linkContainer}>
                            {loadingDraft ? (
                              <Spinner className={styles.depositForm__reminder__loading} />
                            ) : (
                              <a
                                className={clsx(
                                  styles.depositForm__reminder__link,
                                  'vc-btn vc-btn--large vc-btn--full vc-btn--notfull--md-up',
                                )}
                                href="/my-account.shtml#produits"
                                onClick={handleDraftLoader}
                              >
                                {existingDraftCount > 1
                                  ? t('DEPOSIT_FORM.COMPLETE_DRAFTS_PLURAL')
                                  : t('DEPOSIT_FORM.COMPLETE_DRAFT_SINGLE')}
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                      {loading ? (
                        <div className={clsx(styles.depositForm__container)}>
                          <Spinner />
                        </div>
                      ) : (
                        <div
                          id="vc-preduct-add-form"
                          className={clsx(styles.depositForm__form)}
                        >
                          <fieldset className={clsx(styles.depositForm__form__subsection)}>
                            <p className={clsx(styles.depositForm__form__subtitle, 'vc-text-m')}>
                              {t('DEPOSIT_FORM.CATEGORIES')}
                            </p>
                            <div
                              className={clsx('vc-formUnit', styles.depositForm__form__fieldList, 'vc-text-m')}
                              onChange={handleChangeUniverse}
                            >
                              {universes.length &&
                                universes?.map((item, index) => (
                                  <label
                                    key={`${item.title}`}
                                    className={clsx(
                                      styles.depositForm__form__field,
                                      listingDraftPayload.universe === item.id &&
                                        styles['depositForm__form__field--selected'],
                                    )}
                                    htmlFor={`${item.title}-${index}`}
                                  >
                                    <input
                                      id={`${item.title}-${index}`}
                                      type="radio"
                                      name="universe"
                                      value={item.id}
                                      defaultChecked={item.id === listingDraftPayload.universe}
                                      className={clsx('vc-radio__input')}
                                      data-input-text-value={item.title}
                                    />
                                    <span className={clsx(styles.depositForm__form__universeLabel, 'vc-radio__label')}>
                                      {item.title}
                                    </span>
                                  </label>
                                ))}
                            </div>
                          </fieldset>
                          <fieldset className={clsx(styles.depositForm__form__subsection)}>
                            <label
                              htmlFor="preductAddCategory"
                              className={clsx(styles.depositForm__form__subtitle, 'vc-text-m')}
                            >
                              {t('DEPOSIT_FORM.PRODUCT')}
                            </label>
                            <div
                              className={clsx(
                                'vc-formUnit',
                                'vc-formUnit--select',
                                styles['depositForm__form--select'],
                              )}
                            >
                              <select
                                id="preductAddCategory"
                                name="page"
                                className={clsx(
                                  'vc-inputText',
                                  styles.depositForm__form__select,
                                  !listingDraftPayload.category && styles['depositForm__form__select--empty'],
                                )}
                                data-error-required="Required field"
                                onChange={handleChangeCategory}
                                value={listingDraftPayload.category}
                              >
                                <option
                                  className={clsx(styles.depositForm__form__select__firstChoice)}
                                  value=""
                                >
                                  {t('DEPOSIT_FORM.SELECT_CHOOSE')}
                                </option>
                                {category &&
                                  category.map((item) => (
                                    <optgroup
                                      label={item.title}
                                      key={item.id}
                                    >
                                      {item.subCategories.map((subItem) => (
                                        <option
                                          value={subItem.id}
                                          key={subItem.id}
                                        >
                                          {subItem.title}
                                        </option>
                                      ))}
                                    </optgroup>
                                  ))}
                              </select>
                            </div>
                          </fieldset>
                          <fieldset className={clsx(styles.depositForm__form__subsection)}>
                            <label
                              htmlFor="depositForm__form__brands-input"
                              className={clsx(styles.depositForm__form__subtitle, 'vc-text-m')}
                            >
                              {t('DEPOSIT_FORM.BRAND')}
                            </label>
                            <div className={clsx('vc-formUnit', `${brandHasError ? 'error' : ''}`)}>
                              <input
                                id="depositForm__form__brands-input"
                                name="depositForm__form__brands-input"
                                autoComplete="off"
                                className={clsx(
                                  'vc-inputText',
                                  styles.depositForm__form__inputText,
                                  brandSearchTerm && styles['depositForm__form__inputText--hasValue'],
                                )}
                                onChange={handleChangeBrand}
                                onKeyDown={handleKeyboardSelect}
                                onFocus={handleFocusBrand}
                                value={brandSearchTerm}
                                ref={inputBrands}
                                placeholder={`   ${t('DEPOSIT_FORM.BRAND_NAME_PLACEHOLDER')}`}
                              />
                              <ul
                                ref={optionsElement}
                                className={clsx(styles.depositForm__form__optionsList, 'vc-d-none')}
                              >
                                {brandsByFilter.length > 0 ? (
                                  brandsByFilter.map((brand, index) => (
                                    <li key={brand.id}>
                                      <Button
                                        disableDefaultStyling
                                        data-cy="sell-clothes-online-select-brand-button"
                                        className={clsx(
                                          styles.depositForm__form__optionsList__item,
                                          keyboardSelectIndex === index &&
                                            styles['depositForm__form__optionsList__item--selected'],
                                        )}
                                        onClick={() => {
                                          handleSelectBrand(brand);
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            handleSelectBrand(brand);
                                          }
                                        }}
                                        tabIndex={0}
                                      >
                                        <BrandNameWithHighlight
                                          brandName={brand.name}
                                          searchTerm={brandSearchTerm}
                                        />
                                      </Button>
                                    </li>
                                  ))
                                ) : (
                                  <li>
                                    {!isKr && (
                                      <Button
                                        data-cy="sell-clothes-online-add-new-brand-button"
                                        disableDefaultStyling
                                        className={clsx(styles.depositForm__form__optionsList__item)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            handleAddNewBrand();
                                          }
                                        }}
                                        onClick={handleAddNewBrand}
                                        tabIndex={0}
                                      >
                                        {t('DEPOSIT_FORM.ADD_BRAND')} <em>{brandSearchTerm}</em>
                                      </Button>
                                    )}
                                  </li>
                                )}
                              </ul>
                              {brandHasError ? (
                                <p className="vc-inputText-errorMsg vc-text-s">
                                  {brandLengthError
                                    ? t('DEPOSIT_FORM.BRAND_LENGTH_ERROR', { limitCharacter: BRAND_MAX_LENGTH })
                                    : brandHasError
                                      ? t('DEPOSIT_FORM.BRAND_ERROR')
                                      : ''}
                                </p>
                              ) : null}
                            </div>
                          </fieldset>
                          <div className={clsx(styles.depositForm__form__submit)}>
                            <Button
                              type="submit"
                              id="vc-preduct-add-submit"
                              variant="primary"
                              size="large"
                              fullWidth
                              notFullWidthQuery="md-up"
                              disabled={disabled}
                              onClick={handleSubmitForm}
                            >
                              {t('DEPOSIT_FORM.COMPLETE_DRAFTS_PLURAL')}
                            </Button>
                          </div>
                          {preductFormError && (
                            <div className={clsx(styles.depositForm__form__error)}>{preductFormError}</div>
                          )}
                        </div>
                      )}
                    </section>
                    <ul className={clsx(styles.depositForm__reassurance)}>
                      <li className={clsx(styles.depositForm__reassurance__item)}>
                        <Icon
                          className={clsx(styles.depositForm__reassurance__icon)}
                          name="label-large"
                        />
                        <div className={clsx(styles.depositForm__reassurance__title)}>
                          {t('DEPOSIT_FORM.REASSURANCE.CONCEPT.TITLE')}
                        </div>
                        <p className={clsx(styles.depositForm__reassurance__text)}>
                          {t('DEPOSIT_FORM.REASSURANCE.CONCEPT.TEXT')}
                        </p>
                        <a
                          className={clsx(styles.depositForm__reassurance__link)}
                          href={t('DEPOSIT_FORM.REASSURANCE.CONCEPT.LINK_URL')}
                        >
                          {t('DEPOSIT_FORM.REASSURANCE.CONCEPT.LINK')}
                        </a>
                      </li>
                      <li className={clsx(styles.depositForm__reassurance__item)}>
                        <Icon
                          className={clsx(styles.depositForm__reassurance__icon)}
                          name="me-large"
                        />
                        <div className={clsx(styles.depositForm__reassurance__title)}>
                          {t('DEPOSIT_FORM.REASSURANCE.SELLER_PRO.TITLE')}
                        </div>
                        <p className={clsx(styles.depositForm__reassurance__text)}>
                          {t('DEPOSIT_FORM.REASSURANCE.SELLER_PRO.TEXT')}
                        </p>
                        <a
                          className={clsx(styles.depositForm__reassurance__link)}
                          href={t('DEPOSIT_FORM.REASSURANCE.SELLER_PRO.LINK_URL')}
                        >
                          {t('DEPOSIT_FORM.REASSURANCE.SELLER_PRO.LINK')}
                        </a>
                      </li>
                    </ul>
                    {language === 'fr' && (
                      <p className={clsx(styles.depositForm__warningMsg)}>
                        Si vous agissez à titre professionnel, vous devez créer un compte professionnel en adressant
                        votre demande à contactpro@vestiairecollective.com. A défaut, en application de l’
                        <a
                          href="https://www.legifrance.gouv.fr/affichCodeArticle.do?cidTexte=LEGITEXT000006069565&amp;idArticle=LEGIARTI000006292182"
                          target="_blank"
                          rel="noreferrer noopener"
                          className={clsx(styles.depositForm__warningMsg__link)}
                        >
                          article L 132-2 du Code de la consommation
                        </a>{' '}
                        qui sanctionne les pratiques commerciales trompeuses, vous encourez notamment une peine
                        d’emprisonnement de 2 ans et une amende de 300 000 euros.
                      </p>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          {loading || (!isAuthenticated && <div className={clsx(styles.depositForm__pageOverlay)} />)}
        </main>
      </PageAuthenticationGuard>
    </>
  );
};

SellClothesOnlinePage.Layout = [DefaultLayout, SellLayout];
SellClothesOnlinePage.PageType = 'SellClothesOnlinePage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale } = extractFromContext(context);
  const translations = await serverSideTranslations(locale, ['default-layout', 'deposit-form', 'common']);
  return {
    props: {
      ...translations,
    },
  };
};

export default SellClothesOnlinePage;
